import type { EntityState } from '@reduxjs/toolkit';

import { chatEntity } from '~entities/chats/chatEntity';
import type { TMessage } from '~entities/messages';
import { getBotsApi } from '~services/api/bots/dynamic';
import { SUGGESTIONS_RESPONSE_TIMEOUT_MS } from '~services/api/dialogs/constants';
import { timeout } from '~services/utils';
import { api, apiTags } from '~shared/api/origin';
import {
	adaptAudioMessage,
	adaptSextingMessage,
	adaptVideoMessage,
	addEncodedTextFieldToMessage,
	addGifPath,
	formatIMGPaths,
	normalizeFormat,
	transformMessageBodyToMessage,
} from '~utils/messages';

import { messagesAdapter, messagesSelector } from './lib';
import type { SendMessageApiRequest, TSuggestedTextResponse } from './types';

export const {
	endpoints,
	util,
	useGetMessagesQuery,
	useReadMessageMutation,
	useSendMessageMutation,
	useRegenerateMessageInplaceMutation,
	useSelectRegeneratedMessageMutation,
	useDeleteMessageMutation,
	useBuyMessageMutation,
	useClearBotMemoryMutation,
	useGetBotSuggestedTextQuery,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getMessages: build.query<
			{
				receivedCount: number;
				messages: EntityState<TMessage>;
				totalLength: number;
			},
			{
				id: string;
				recipientId: string;
				omit: number;
			}
		>({
			providesTags: (result, error, { recipientId }) => [
				{ type: apiTags.messages, id: recipientId },
			],
			query: ({ id, recipientId, omit }) => ({
				url: `v2/dialogs/messages/sender/${id}/recipient/${recipientId}`,
				params: { omit, select: 30 },
				headers: {
					Accept: 'application/json+videos',
				},
			}),
			transformResponse: (response: TMessage[] | null) => {
				if (response === null) {
					return {
						receivedCount: 0,
						totalLength: 0,
						messages: messagesAdapter.getInitialState(),
					};
				}
				const transformed = response
					?.map(addEncodedTextFieldToMessage)
					.map(adaptAudioMessage)
					.map(normalizeFormat)
					.map(addGifPath)
					.map(formatIMGPaths)
					.map(adaptSextingMessage)
					.map(adaptVideoMessage);

				const messages = messagesAdapter.upsertMany(
					messagesAdapter.getInitialState(),
					transformed
				);
				return {
					receivedCount: response.length,
					messages,
					totalLength: messages.ids.length,
				};
			},
			forceRefetch: ({ currentArg, endpointState }) => {
				return (
					// @ts-ignore
					(currentArg?.omit ?? 0) >= (endpointState?.data?.totalLength ?? 0)
				);
			},
			serializeQueryArgs: ({
				endpointName,
				queryArgs: { recipientId, id },
			}) => {
				return `${endpointName}-${recipientId}-${id}`;
			},
			merge: (currentState, incomingState) => {
				messagesAdapter.setAll(currentState.messages, [
					...messagesSelector.selectAll(incomingState.messages),
					...messagesSelector.selectAll(currentState.messages),
				]);
				currentState.receivedCount = incomingState.receivedCount;
				currentState.totalLength =
					currentState.totalLength + incomingState.receivedCount;
			},
			// Example invalidate with param
			/*providesTags: (result, error, { recipientId }) => [
				{ type: apiTags.messages, id: recipientId }, // ? as const
			],*/
		}),
		readMessage: build.mutation<
			undefined,
			{ senderId: string; recipientId: string; messageId: number }
		>({
			query: ({ senderId, recipientId, messageId }) => ({
				// TODO: This endpoint mentioned in CS, but does not not work for me, 202 Accepted, but counter is not reseted
				// url: `/dialogs/messages/sender/${senderId}/recipient/${recipientId}/${messageId}`,
				url: `/dialogs/messages/${senderId}:${recipientId}/${messageId}`,
				method: 'PATCH',
				body: JSON.stringify({ read: true }),
				headers: {
					'content-type': 'application/json',
				},
			}),
			async onQueryStarted(
				{ senderId, recipientId },
				{ getState, dispatch, queryFulfilled }
			) {
				const botsApi = await getBotsApi();
				let unreadCount = 0;
				const chatPatches = chatEntity.update(getState, dispatch, (draft) => {
					const { botId } = draft;

					if (botId === recipientId || botId === senderId) {
						unreadCount = draft.counters.unreadCount;
						draft.counters.unreadCount = 0;
					}
				});
				const countsPatch = dispatch(
					botsApi.util.updateQueryData(
						'getChatsCount',
						{ userId: recipientId },
						(draft) => {
							draft.unreadMessagesCount = Math.max(
								0,
								draft.unreadMessagesCount - unreadCount
							);
						}
					)
				);

				queryFulfilled.catch(() => {
					chatPatches.forEach((patch) => patch.undo());
					countsPatch.undo();
				});
			},
		}),
		clearBotMemory: build.mutation<
			{},
			{
				userId: string;
				recipientId: string;
				deleteFrom?: string;
				deleteAll?: boolean;
			}
		>({
			query: ({ userId, recipientId, deleteFrom, deleteAll }) => ({
				url: `/dialogs/messages/${userId}:${recipientId}/period`,
				method: 'DELETE',
				params: {
					deleteAll,
					deleteFrom,
				},
			}),
			invalidatesTags: (result, error, { recipientId }) => [
				{ type: apiTags.messages, id: recipientId },
			],
			async onQueryStarted(
				{ userId, recipientId },
				{ dispatch, queryFulfilled }
			) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getMessages',
							{ id: userId, recipientId, omit: 0 },
							(draft) => {
								draft.totalLength = 0;
								draft.receivedCount = 0;
								draft.messages = messagesAdapter.getInitialState();
							}
						)
					);
				} catch {}
			},
		}),
		getBotSuggestedText: build.query<
			TSuggestedTextResponse,
			{
				userId: string;
				recipientId: string;
			}
		>({
			async queryFn(
				{ userId, recipientId },
				_queryApi,
				_extraOptions,
				fetchWithBQ
			) {
				const response = await Promise.race([
					fetchWithBQ(`/ai/suggestions/${userId}/${recipientId}`),
					timeout(SUGGESTIONS_RESPONSE_TIMEOUT_MS),
				]);

				return {
					data: response.error
						? {
								messageId: 0,
								suggestions: [],
						  }
						: (response.data as TSuggestedTextResponse),
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.userId}-${queryArgs.recipientId}`;
			},
			providesTags: [apiTags.suggestedTexts],
		}),
		sendMessage: build.mutation<
			TMessage[],
			{ id: string; recipientId: string; message: SendMessageApiRequest }
		>({
			query: ({ id, recipientId, message }) => ({
				url: `/dialogs/messages/sender/${id}/recipient/${recipientId}`,
				method: 'POST',
				body: message,
			}),
			async onQueryStarted(
				{ id, recipientId, message },
				{ getState, dispatch, queryFulfilled }
			) {
				const messagesPatch = dispatch(
					util.updateQueryData(
						'getMessages',
						{ id, recipientId, omit: 0 },
						(draft) => {
							const lastTag = messagesSelector.selectIds(draft.messages)[
								messagesSelector.selectTotal(draft.messages) - 1
							];
							const prevId =
								messagesSelector.selectById(draft.messages, lastTag)?.id || 0;

							const msg = transformMessageBodyToMessage(
								message,
								id,
								recipientId,
								prevId
							);
							messagesAdapter.addOne(draft.messages, msg);
							draft.totalLength += 1;
						}
					)
				);
				const botsApi = await getBotsApi();
				let unreadCount = 0;
				const chatPatches = chatEntity.update(getState, dispatch, (draft) => {
					if (draft.botId === recipientId) {
						draft.counters.totalCount += 1;
						unreadCount = draft.counters.unreadCount;
						draft.counters.unreadCount = 0;
						draft.lastMessage = {
							id: Date.now(),
							senderId: id,
							recipientId,
							text: message.text,
							isSexting: false,
							paid: true,
							timestamp: Date.now(),
						};
					}
				});
				const suggestionsPatch = dispatch(
					util.updateQueryData(
						'getBotSuggestedText',
						{
							userId: id,
							recipientId,
						},
						(draft) => {
							draft.suggestions = null;
						}
					)
				);
				const countsPatch = dispatch(
					botsApi.util.updateQueryData(
						'getChatsCount',
						{ userId: recipientId },
						(draft) => {
							draft.unreadMessagesCount = Math.max(
								0,
								draft.unreadMessagesCount - unreadCount
							);
						}
					)
				);

				queryFulfilled.catch(() => {
					messagesPatch.undo();
					chatPatches.forEach((patch) => patch.undo());
					suggestionsPatch.undo();
					countsPatch.undo();
				});
			},
		}),
		regenerateMessageInplace: build.mutation<
			{},
			{ userId: string; recipientId: string; messageId: number }
		>({
			query: ({ userId, recipientId, messageId }) => ({
				url: `/ai/users/${userId}/${recipientId}/regenerate/inplace`,
				method: 'POST',
				body: {
					messageId,
				},
			}),
		}),
		selectRegeneratedMessage: build.mutation<
			{},
			{ userId: string; recipientId: string; messageId: number; index: number }
		>({
			query: ({ userId, recipientId, messageId, index }) => ({
				url: `/ai/users/${userId}/${recipientId}/variant/select`,
				method: 'POST',
				body: {
					messageId,
					index,
				},
			}),
		}),
		deleteMessage: build.mutation<
			{},
			{ senderId: string; recipientId: string; messageId: number }
		>({
			query: ({ senderId, recipientId, messageId }) => ({
				url: `/dialogs/messages/${senderId}:${recipientId}/${messageId}`,
				method: 'DELETE',
			}),
			onQueryStarted(
				{ senderId, recipientId, messageId },
				{ dispatch, queryFulfilled }
			) {
				const deleteDispatch = dispatch(
					util.updateQueryData(
						'getMessages',
						{ id: recipientId, recipientId: senderId, omit: 0 },
						(draft) => {
							const allMes = messagesSelector.selectAll(draft.messages);
							const message = allMes.find(({ id }) => id === messageId);

							if (!message) return;
							messagesAdapter.removeOne(draft.messages, message.tag);
						}
					)
				);

				queryFulfilled.catch(() => {
					deleteDispatch.undo();
				});
			},
		}),
		buyMessage: build.mutation<
			{ Success: boolean; Data: { Text: string } },
			{ id: string; recipientId: string; messageId: number; mediaId?: string }
		>({
			query: ({ id, recipientId, messageId }) => ({
				url: `/dialogs/messages/sender/${recipientId}/recipient/${id}/${messageId}/buy`,
				method: 'POST',
			}),
			async onQueryStarted(
				{ id, recipientId, messageId },
				{ getState, dispatch, queryFulfilled }
			) {
				try {
					const { data: res } = await queryFulfilled;
					if (!res.Success) return;
					const text = res.Data.Text;
					dispatch(
						util.updateQueryData(
							'getMessages',
							{ id, recipientId, omit: 0 },
							(draft) => {
								const message = messagesSelector
									.selectAll(draft.messages)
									.find(({ id }) => id === messageId);
								if (!message) {
									return;
								}
								messagesAdapter.updateOne(draft.messages, {
									id: message.tag,
									changes: {
										text,
										meta: {
											...message.meta,
											tariffication: {
												...message.meta?.tariffication,
												paid: true,
												final: true,
												cost: 0,
											},
										},
									},
								});
							}
						)
					);

					chatEntity.update(getState, dispatch, (draft) => {
						if (
							draft.botId === recipientId &&
							draft.lastMessage?.id === messageId
						) {
							draft.lastMessage.paid = true;
							draft.lastMessage.cost = 0;
							draft.lastMessage.text = text;
						}
					});
				} catch {}
			},
		}),
	}),
});
